import { Controller } from "@hotwired/stimulus"

// Live summary line for the recurring section. Reads pattern, days, time, and
// bound fields from inside the controller's element + the time hidden input
// (rendered outside this element). Updates a target on any relevant change.
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
const PATTERN_LABELS = { weekly: "weekly", biweekly: "every 2 weeks", monthly: "monthly" }

export default class extends Controller {
  static targets = ["text"]

  connect() {
    this.boundRefresh = this.refresh.bind(this)
    this.element.addEventListener("change", this.boundRefresh)
    this.element.addEventListener("day-picker:change", this.boundRefresh)
    document.addEventListener("change", this.boundRefresh)
    this.refresh()
  }

  disconnect() {
    this.element.removeEventListener("change", this.boundRefresh)
    this.element.removeEventListener("day-picker:change", this.boundRefresh)
    document.removeEventListener("change", this.boundRefresh)
  }

  refresh() {
    if (!this.hasTextTarget) return
    this.textTarget.textContent = this.composeSummary()
  }

  composeSummary() {
    const days = this.selectedDays()
    if (days.length === 0) {
      return "Pick a start date and at least one day to repeat on."
    }

    const daysText = this.formatDays(days)
    const timeText = this.formatTime()
    const patternText = this.formatPattern()
    const boundText = this.formatBound()
    return `${daysText} at ${timeText}, ${patternText}${boundText}.`
  }

  selectedDays() {
    const inputs = this.element.querySelectorAll('input[name="appointment[days_of_week][]"]')
    const indices = []
    inputs.forEach((input) => {
      const idx = parseInt(input.value, 10)
      if (!isNaN(idx)) indices.push(idx)
    })
    return Array.from(new Set(indices)).sort((a, b) => a - b)
  }

  formatDays(days) {
    const names = days.map((d) => `${DAY_NAMES[d]}s`)
    if (names.length === 1) return names[0]
    if (names.length === 2) return `${names[0]} and ${names[1]}`
    return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`
  }

  formatTime() {
    const timeInput = document.querySelector('input[type="hidden"][name="appointment[time]"]')
    if (!timeInput || !timeInput.value) return "the time you choose"
    const parts = timeInput.value.split(":")
    if (parts.length !== 2) return "the time you choose"
    let hour = parseInt(parts[0], 10)
    const minute = parts[1]
    if (isNaN(hour)) return "the time you choose"
    const period = hour >= 12 ? "PM" : "AM"
    const displayHour = ((hour + 11) % 12) + 1
    return `${displayHour}:${minute} ${period}`
  }

  formatPattern() {
    const radio = this.element.querySelector('input[name="appointment[recurring_pattern]"]:checked')
    if (!radio) return "weekly"
    return PATTERN_LABELS[radio.value] || radio.value
  }

  formatBound() {
    const boundType = this.element.querySelector('input[name="appointment[bound_type]"]:checked')
    if (!boundType || boundType.value === "open") return ""

    if (boundType.value === "cycles") {
      const countInput = this.element.querySelector('input[name="appointment[ends_after_cycles]"]')
      const count = parseInt(countInput?.value, 10)
      if (isNaN(count) || count <= 0) return ""
      const pattern = this.element.querySelector('input[name="appointment[recurring_pattern]"]:checked')?.value
      const unit = pattern === "monthly" ? "month" : (pattern === "weekly" ? "week" : "cycle")
      return `, ending after ${count} ${count === 1 ? unit : unit + "s"}`
    }

    return ""
  }
}
